import React from 'react';
import {
  Box,
  Grid,
  Card,
  Stack,
  Divider,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';

import { LuPlus } from 'react-icons/lu';
import { IoShieldCheckmark, IoCheckmarkCircle } from 'react-icons/io5';

const SectionNumber = ({ n }) => (
  <Box
    sx={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: `1.5px solid #3FBEAC`,
      color: '#3FBEAC',
      bgcolor: '#ECF9F7',
      fontSize: 12,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {n}
  </Box>
);

const RuleTable = ({ rows }) => (
  <Box sx={{ overflow: 'auto', border: '1px solid #F2F2F2', borderRadius: 3, my: 1, height:300 }}>
    <Table sx={{height:"100%"}} size="small">
      <TableHead>
        <TableRow>
          {['#', 'Rule', 'Condition', '', 'Match', 'Status'].map((h) => (
            <TableCell key={h} sx={{ color: '#535353', fontSize: 12, borderBottom: '1px solid #E8E8E8', textAlign: 'center', py:2 }}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: 'center', py: 2 }}>{row.id}</TableCell>
            <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: 'center', py: 2 }}>{row.rule}</TableCell>
            <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: 'center', py: 2 }}>{row.condition}</TableCell>
            <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: 'center', py: 2 }}>{row.value}</TableCell>
            <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2', textAlign: 'center', py: 2 }}>{row.match}</TableCell>
            <TableCell sx={{ borderBottom: '1px solid #F2F2F2', textAlign: 'center' }}>
              <Chip size="small" label={row.status} sx={{ bgcolor: '#ECF9F7', fontWeight: 600, fontSize: 12, p:1.5 }} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const RulesEligibilityTab = ({ data }) => {
  const { eligibilitySummary, includeUsersRules, excludeUsersRules, ruleHealth, cashbackRules, validationChecklist } = data;

  return (
    <Grid container spacing={2.5} alignItems="stretch">
      {/* Left column */}
      <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, width: '100%', height: '100%' }}>
          {/* Eligibility Summary */}
          <Box my={1} borderBottom="1px solid #E8E8E8" py={2}>
            <Box display="flex" gap={1.5} alignItems="center">
              <SectionNumber n={1} />
              <Typography fontWeight={600}>Eligibility Summary</Typography>
            </Box>
            <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5 }}>
              {eligibilitySummary.subtitle}
            </Typography>

            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Eligible Users
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {eligibilitySummary.eligibleUsers.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Excluded Users
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {eligibilitySummary.excludedUsers.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Pending Check
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {eligibilitySummary.pendingCheck.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                    Total Evaluated
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {eligibilitySummary.totalEvaluated.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="caption" color="#535353" sx={{ display: 'block', mb: 0.5 }}>
                  Eligibility Status
                </Typography>
                <Chip size="small" label={eligibilitySummary.status} sx={{ bgcolor: '#ECF9F7', fontWeight: 600, p:1.5 }} />
              </Box>
            </Box>
          </Box>

          {/* Include Users */}
          <Box my={1} borderBottom="1px solid #E8E8E8" py={2}>
            <Box
              display="flex"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 1.5, sm: 0 }}
            >
              <Box>
                <Box display="flex" gap={1.5} alignItems="center">
                  <SectionNumber n={2} />
                  <Typography fontWeight={600}>Include Users</Typography>
                </Box>
                <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5 }}>
                  Define who is eligible to earn this cashback
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<LuPlus size={16} />}
                sx={{ textTransform: 'none', color: '#159D89', borderColor: '#3FBEAC', fontSize: 13 }}
              >
                Add Condition
              </Button>
            </Box>
            <RuleTable rows={includeUsersRules} />
          </Box>

          {/* Exclude Users */}
          <Box my={1} py={2}>
            <Box
              display="flex"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 1.5, sm: 0 }}
            >
              <Box>
                <Box display="flex" gap={1.5} alignItems="center">
                  <SectionNumber n={3} />
                  <Typography fontWeight={600}>Exclude Users</Typography>
                </Box>
                <Typography variant="body2" color="#535353" sx={{ pl: 4.5, mt: 0.5 }}>
                  Define who is not eligible to earn this cashback
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<LuPlus size={16} />}
                sx={{ textTransform: 'none', color: '#A80101', borderColor: '#A80101', fontSize: 13 }}
              >
                Add Condition
              </Button>
            </Box>
            <RuleTable rows={excludeUsersRules} />
          </Box>
        </Card>
      </Grid>

      {/* Right column */}
      <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          {/* Rule Health */}
          <Card variant="outlined" sx={{ borderRadius: 4, borderColor: '#E8E8E8', p: 1.5 }}>
            <Box sx={{ p: 1, my: 0.5 }}>
              <Typography fontWeight={600}>Rule Health</Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1, py: 2 }}>
              <IoShieldCheckmark color="#3FBEAC" size={25} />
              <Typography variant="body1" fontWeight={700} color="#159D89">
                {ruleHealth.status}
              </Typography>
            </Stack>
            <Typography variant="body2"  sx={{ px: 1 }}>
              {ruleHealth.message}
            </Typography>
            <Typography variant="caption" color="#535353" sx={{ px: 1, pb: 1, display: 'block' }}>
              {ruleHealth.subMessage}
            </Typography>
          </Card>

          {/* Cashback Rules */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, my: 2 }}>
            <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
              <Typography fontWeight={600}>Cashback Rules</Typography>
            </Box>
            <Stack divider={<Divider />}>
              {cashbackRules.map((rule, i) => (
                <Stack key={rule.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.25, gap: 1, flexWrap: 'wrap' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: '#E8E8E8',
                        color: '#535353',
                        fontSize: 11,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography variant="body2">{rule.label}</Typography>
                  </Stack>
                  <Typography variant="body2">
                    {rule.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Card>

          {/* Validation Checklist */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, mt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
              <Typography fontWeight={600}>Validation Checklist</Typography>
            </Box>
            <Stack divider={<Divider />} sx={{ flexGrow: 1 }}>
              {validationChecklist.map((item, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IoCheckmarkCircle color="#3FBEAC" size={18} />
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                  <Chip size="small" label="Pass" sx={{ bgcolor: '#ECF9F7', fontWeight: 600, p:1.5 }} />
                </Stack>
              ))}
            </Stack>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default RulesEligibilityTab;